/**
 * A vertical stacked bar chart — one bar per category (a property), each
 * divided into the same ordered segments (an aging bucket) so a reader can
 * compare both the total and its composition across categories at once.
 * This is the multi-series case `MiniBarChart` deliberately isn't: that one
 * chart is a single ordered severity measurement, this one is that same
 * measurement repeated per category, so segment identity has to survive a
 * glance across bars — hence the legend (skill rule: a legend is always
 * present for ≥2 series).
 *
 * Bars are div-percentage-height, not SVG — same reasoning as
 * `MiniBarChart`: reflows with the page, no viewBox math to keep in sync
 * with a resize. Each segment gets a 2px surface gap from its neighbors
 * (the skill's spacer rule for stacked fills) and a native `title` tooltip
 * with its exact value, since a thin segment can't always hold a label.
 */

export interface StackedBarSegment {
  key: string;
  label: string;
  value: number;
  displayValue: string;
}

export interface StackedBarDatum {
  category: string;
  segments: StackedBarSegment[];
}

export function StackedBarChart({
  data,
  colorVar,
  maxBars = 12,
}: {
  data: StackedBarDatum[];
  /** A CSS custom property name, e.g. '--down'. Segments step light→dark
   *  across `segments`' order, same ramp technique as `MiniBarChart`. */
  colorVar: string;
  /** Caps how many category bars render — a portfolio-wide chart with
   *  dozens of properties turns unreadable well before that; filter to fewer
   *  properties (the report's own filter row) to see the rest. */
  maxBars?: number;
}) {
  const shown = [...data]
    .sort(
      (a, b) =>
        b.segments.reduce((sum, s) => sum + s.value, 0) -
        a.segments.reduce((sum, s) => sum + s.value, 0),
    )
    .slice(0, maxBars);

  const totals = shown.map((d) => d.segments.reduce((sum, s) => sum + s.value, 0));
  const max = Math.max(1, ...totals);
  const segmentLabels = shown[0]?.segments ?? [];

  return (
    <div>
      {segmentLabels.length > 1 ? (
        <div className="row stacked-bar-chart__legend">
          {segmentLabels.map((seg, i) => {
            const mixPct = Math.round(40 + (i / Math.max(1, segmentLabels.length - 1)) * 60);
            return (
              <span key={seg.key} className="muted">
                <span
                  className="stacked-bar-chart__swatch"
                  style={{
                    background: `color-mix(in srgb, var(${colorVar}) ${mixPct}%, var(--surface))`,
                  }}
                />
                {seg.label}
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="stacked-bar-chart">
        {shown.map((datum, barIndex) => (
          <div className="stacked-bar-chart__col" key={datum.category}>
            <div className="stacked-bar-chart__track" style={{ height: '10rem' }}>
              <div
                className="stacked-bar-chart__stack"
                style={{ height: `${(totals[barIndex]! / max) * 100}%` }}
              >
                {[...datum.segments].reverse().map((seg, revIndex) => {
                  const i = datum.segments.length - 1 - revIndex;
                  const mixPct = Math.round(40 + (i / Math.max(1, datum.segments.length - 1)) * 60);
                  if (seg.value <= 0) return null;
                  return (
                    <div
                      key={seg.key}
                      className="stacked-bar-chart__segment"
                      title={`${datum.category} — ${seg.label}: ${seg.displayValue}`}
                      style={{
                        height: `${(seg.value / totals[barIndex]!) * 100}%`,
                        background: `color-mix(in srgb, var(${colorVar}) ${mixPct}%, var(--surface))`,
                      }}
                    />
                  );
                })}
              </div>
            </div>
            <span className="stacked-bar-chart__total">
              {totals[barIndex] === 0
                ? '—'
                : new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  }).format(totals[barIndex]!)}
            </span>
            <span className="stacked-bar-chart__label" title={datum.category}>
              {datum.category}
            </span>
          </div>
        ))}
      </div>
      {data.length > shown.length ? (
        <p className="muted">
          Showing the {shown.length} highest of {data.length} properties — narrow with the filter
          above to see the rest.
        </p>
      ) : null}
    </div>
  );
}
